import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Edit2, Copy, FileText, Save, X, Star, Calendar, Mail, Phone, Hash, Type, AlignLeft, List, CheckSquare, CircleDot, Upload, ListChecks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${API_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'dropdown', label: 'Dropdown', icon: List },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'radio', label: 'Radio Buttons', icon: CircleDot },
  { value: 'file', label: 'File Upload', icon: Upload },
  { value: 'multiselect', label: 'Multi-Select', icon: ListChecks },
  { value: 'textarea', label: 'Text Area', icon: AlignLeft },
  { value: 'rating', label: 'Rating', icon: Star },
];

const AdminFormsPage = () => {
  const { user } = useOutletContext();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', fields: [] });
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingFieldIndex, setEditingFieldIndex] = useState(-1);

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForms(response.data);
    } catch (error) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = () => {
    setEditingForm(null);
    setFormData({ title: '', description: '', fields: [] });
    setShowFormBuilder(true);
  };

  const handleEditForm = (form) => {
    setEditingForm(form);
    setFormData({
      title: form.title,
      description: form.description || '',
      fields: form.fields || []
    });
    setShowFormBuilder(true);
  };

  const handleSaveForm = async () => {
    if (!formData.title.trim()) {
      toast.error('Form title is required');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      const payload = {
        title: formData.title,
        description: formData.description,
        fields: formData.fields.map((f, idx) => ({ ...f, order: idx }))
      };

      if (editingForm) {
        await axios.put(`${API}/forms/${editingForm.form_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Form updated successfully');
      } else {
        await axios.post(`${API}/forms`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Form created successfully');
      }

      setShowFormBuilder(false);
      loadForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save form');
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form?')) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/forms/${formId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Form deleted');
      loadForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete form');
    }
  };

  const handleDuplicateForm = async (form) => {
    try {
      const token = localStorage.getItem('session_token');
      const payload = {
        title: `${form.title} (Copy)`,
        description: form.description,
        fields: form.fields.map(f => ({ ...f, field_id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 8)}` }))
      };
      await axios.post(`${API}/forms`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Form duplicated');
      loadForms();
    } catch (error) {
      toast.error('Failed to duplicate form');
    }
  };

  const addField = () => {
    setEditingField({
      field_id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`,
      field_type: 'text',
      label: '',
      placeholder: '',
      required: false,
      options: [],
      max_rating: 5,
      order: formData.fields.length
    });
    setEditingFieldIndex(-1);
    setShowFieldEditor(true);
  };

  const editField = (field, index) => {
    setEditingField({ ...field });
    setEditingFieldIndex(index);
    setShowFieldEditor(true);
  };

  const saveField = () => {
    if (!editingField.label.trim()) {
      toast.error('Field label is required');
      return;
    }

    // Validate options for dropdown/radio/checkbox/multiselect
    if (['dropdown', 'radio', 'checkbox', 'multiselect'].includes(editingField.field_type)) {
      if (!editingField.options || editingField.options.length === 0) {
        toast.error('Please add at least one option');
        return;
      }
    }

    const newFields = [...formData.fields];
    if (editingFieldIndex >= 0) {
      newFields[editingFieldIndex] = editingField;
    } else {
      newFields.push(editingField);
    }

    setFormData({ ...formData, fields: newFields });
    setShowFieldEditor(false);
    setEditingField(null);
  };

  const deleteField = (index) => {
    const newFields = formData.fields.filter((_, i) => i !== index);
    setFormData({ ...formData, fields: newFields });
  };

  const moveField = (index, direction) => {
    const newFields = [...formData.fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFormData({ ...formData, fields: newFields });
  };

  const addOption = () => {
    const newOptions = [...(editingField.options || []), { label: '', value: '' }];
    setEditingField({ ...editingField, options: newOptions });
  };

  const updateOption = (index, key, value) => {
    const newOptions = [...editingField.options];
    newOptions[index] = { ...newOptions[index], [key]: value };
    // Auto-fill value from label if value is empty
    if (key === 'label' && !newOptions[index].value) {
      newOptions[index].value = value.toLowerCase().replace(/\s+/g, '_');
    }
    setEditingField({ ...editingField, options: newOptions });
  };

  const removeOption = (index) => {
    const newOptions = editingField.options.filter((_, i) => i !== index);
    setEditingField({ ...editingField, options: newOptions });
  };

  const getFieldIcon = (type) => {
    const field = FIELD_TYPES.find(f => f.value === type);
    return field ? field.icon : Type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#D4AF37' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-forms-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Design Form</h1>
          <p className="text-sm text-muted-foreground">Create custom registration forms for events</p>
        </div>
        <Button
          onClick={handleCreateForm}
          style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
          data-testid="create-form-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create New Form
        </Button>
      </div>

      {forms.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No Forms Created</h3>
            <p className="text-muted-foreground mb-4">Create your first form to collect event registrations</p>
            <Button onClick={handleCreateForm} style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}>
              <Plus className="w-4 h-4 mr-2" />
              Create Form
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {forms.map((form) => (
            <Card key={form.form_id} className="hover:shadow-lg transition-shadow" data-testid={`form-card-${form.form_id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between">
                  <span className="text-lg truncate">{form.title}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                    {form.fields?.length || 0} fields
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {form.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{form.description}</p>
                )}
                <p className="text-xs text-muted-foreground mb-4">
                  Created: {formatDate(form.created_at)}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditForm(form)}
                    data-testid={`edit-form-${form.form_id}`}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDuplicateForm(form)}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteForm(form.form_id)}
                    data-testid={`delete-form-${form.form_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Builder Dialog */}
      <Dialog open={showFormBuilder} onOpenChange={setShowFormBuilder}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingForm ? 'Edit Form' : 'Create New Form'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Form Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Event Registration Form"
                  data-testid="form-title-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the form"
                />
              </div>
            </div>

            <div className="border rounded-lg p-4 bg-slate-50">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Form Fields</h3>
                <Button onClick={addField} size="sm" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} data-testid="add-field-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Field
                </Button>
              </div>

              {formData.fields.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No fields added yet. Click &quot;Add Field&quot; to start building your form.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.fields.map((field, index) => {
                    const FieldIcon = getFieldIcon(field.field_type);
                    return (
                      <div
                        key={field.field_id}
                        className="flex items-center gap-2 p-3 bg-white rounded-lg border hover:shadow-sm"
                        data-testid={`form-field-${index}`}
                      >
                        <GripVertical className="w-4 h-4 text-muted-foreground" />
                        <FieldIcon className="w-4 h-4" style={{ color: '#D4AF37' }} />
                        <div className="flex-1">
                          <span className="font-medium">{field.label || 'Untitled'}</span>
                          <span className="text-xs text-muted-foreground ml-2">({field.field_type})</span>
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => moveField(index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => moveField(index, 'down')}
                            disabled={index === formData.fields.length - 1}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => editField(field, index)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-600"
                            onClick={() => deleteField(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFormBuilder(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveForm} style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} data-testid="save-form-btn">
              <Save className="w-4 h-4 mr-2" />
              {editingForm ? 'Update Form' : 'Create Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field Editor Dialog */}
      <Dialog open={showFieldEditor} onOpenChange={setShowFieldEditor}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFieldIndex >= 0 ? 'Edit Field' : 'Add Field'}</DialogTitle>
          </DialogHeader>

          {editingField && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Field Type *</Label>
                <Select
                  value={editingField.field_type}
                  onValueChange={(value) => setEditingField({ ...editingField, field_type: value })}
                >
                  <SelectTrigger data-testid="field-type-select">
                    <SelectValue placeholder="Select field type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Field Label *</Label>
                <Input
                  value={editingField.label}
                  onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                  placeholder="e.g., Full Name"
                  data-testid="field-label-input"
                />
              </div>

              <div>
                <Label>Placeholder</Label>
                <Input
                  value={editingField.placeholder || ''}
                  onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                  placeholder="e.g., Enter your full name"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={editingField.required}
                  onCheckedChange={(checked) => setEditingField({ ...editingField, required: checked })}
                  data-testid="field-required-switch"
                />
                <Label>Required field</Label>
              </div>

              {/* Options for dropdown/radio/checkbox/multiselect */}
              {['dropdown', 'radio', 'checkbox', 'multiselect'].includes(editingField.field_type) && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label>Options</Label>
                    <Button size="sm" variant="outline" onClick={addOption}>
                      <Plus className="w-3 h-3 mr-1" />
                      Add Option
                    </Button>
                  </div>
                  {editingField.options?.map((option, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input
                        value={option.label}
                        onChange={(e) => updateOption(idx, 'label', e.target.value)}
                        placeholder="Option label"
                        className="flex-1"
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-red-600"
                        onClick={() => removeOption(idx)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Rating max value */}
              {editingField.field_type === 'rating' && (
                <div>
                  <Label>Max Rating</Label>
                  <Select
                    value={String(editingField.max_rating || 5)}
                    onValueChange={(value) => setEditingField({ ...editingField, max_rating: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[3, 5, 10].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n} Stars</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFieldEditor(false)}>
              Cancel
            </Button>
            <Button onClick={saveField} style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} data-testid="save-field-btn">
              {editingFieldIndex >= 0 ? 'Update Field' : 'Add Field'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFormsPage;
